<template>
    <div class="nl-test-panel">
      <div class="test-header">
        <h2><i class="fas fa-flask"></i> NL Search Test Lab</h2>
        <p class="subtitle">Test query expansion, retrieval, and reranking</p>
      </div>
  
      <!-- Search Input -->
      <div class="search-section">
        <div class="input-group">
          <input
            v-model="query"
            @keyup.enter="runSearch"
            type="text"
            placeholder="Enter search query (e.g., 'ai research engineer' or 'php symfony backend')"
            class="query-input"
          />
          <button @click="runSearch" :disabled="isLoading" class="search-btn">
            <i v-if="!isLoading" class="fas fa-search"></i>
            <i v-else class="fas fa-spinner fa-spin"></i>
            Search
          </button>
        </div>
  
        <!-- Options -->
        <div class="options-row">
          <label class="option">
            <input type="checkbox" v-model="useReranking" />
            <span>Use LLM Reranking</span>
          </label>
          <label class="option">
            <input type="checkbox" v-model="showDebug" />
            <span>Show Debug Info</span>
          </label>
          <div class="size-inputs">
            <label>
              Retrieve:
              <input type="number" v-model.number="retrievalSize" min="10" max="100" />
            </label>
            <label>
              Return:
              <input type="number" v-model.number="rerankSize" min="5" max="50" />
            </label>
          </div>
        </div>
      </div>
  
      <!-- Results Section -->
      <div v-if="searchResult" class="results-section">
        <!-- Summary Bar -->
        <div class="summary-bar">
          <div class="summary-item">
            <span class="label">Query Interpretation:</span>
            <span class="value">{{ searchResult.interpretation }}</span>
          </div>
          <div class="summary-item">
            <span class="label">Core Role:</span>
            <span class="value highlight">{{ searchResult.core_role }}</span>
          </div>
          <div class="summary-item">
            <span class="label">Variants Generated:</span>
            <span class="value">{{ searchResult.variants_count }}</span>
          </div>
          <div class="summary-item">
            <span class="label">Total Hits:</span>
            <span class="value">{{ searchResult.total_hits }}</span>
          </div>
          <div class="summary-item">
            <span class="label">Time:</span>
            <span class="value">{{ searchResult.elapsed_seconds?.toFixed(2) }}s</span>
          </div>
        </div>
  
        <!-- Rerank Reasoning -->
        <div v-if="searchResult.rerank_reasoning && useReranking" class="rerank-reasoning">
          <h4><i class="fas fa-brain"></i> Reranking Reasoning</h4>
          <p>{{ searchResult.rerank_reasoning }}</p>
        </div>
  
        <!-- Results List -->
        <div class="results-list">
          <div
            v-for="(result, index) in searchResult.results"
            :key="result.document_id"
            class="result-card"
          >
            <div class="result-header">
              <span class="rank">#{{ index + 1 }}</span>
              <span class="name">{{ result.name }}</span>
              <span class="score" :title="'ES Score: ' + result.es_score">
                {{ result.es_score?.toFixed(1) }}
              </span>
            </div>
  
            <!-- Job Titles -->
            <div class="job-titles">
              <span
                v-for="exp in result.work_experience?.slice(0, 3)"
                :key="exp.job_title"
                class="job-title-chip"
              >
                {{ exp.job_title }}
              </span>
            </div>
  
            <!-- Skills -->
            <div class="skills-row">
              <span
                v-for="skill in result.skills?.technical?.slice(0, 8)"
                :key="skill"
                class="skill-chip"
              >
                {{ skill }}
              </span>
              <span v-if="result.skills?.technical?.length > 8" class="more">
                +{{ result.skills.technical.length - 8 }} more
              </span>
            </div>
  
            <!-- Summary (truncated) -->
            <p class="summary">{{ truncate(result.candidate_summary, 200) }}</p>
  
            <!-- Candidate ID for reference -->
            <div class="meta-row">
              <code class="candidate-id">{{ result.metadata?.candidate_id }}</code>
              <code class="doc-id">{{ result.document_id?.slice(0, 12) }}...</code>
            </div>
          </div>
        </div>
      </div>
  
      <!-- Debug Panel -->
      <div v-if="showDebug && searchResult?.debug" class="debug-panel">
        <h3><i class="fas fa-bug"></i> Debug Info</h3>
        
        <!-- Expansion -->
        <details>
          <summary>Query Expansion</summary>
          <pre>{{ JSON.stringify(searchResult.debug.expansion, null, 2) }}</pre>
        </details>
  
        <!-- ES Query -->
        <details>
          <summary>Elasticsearch Query</summary>
          <pre>{{ JSON.stringify(searchResult.debug.es_query, null, 2) }}</pre>
        </details>
  
        <!-- Retrieval Stats -->
        <details v-if="searchResult.debug.retrieval">
          <summary>Retrieval Stats</summary>
          <pre>{{ JSON.stringify(searchResult.debug.retrieval, null, 2) }}</pre>
        </details>
  
        <!-- Reranking -->
        <details v-if="searchResult.debug.reranking">
          <summary>Reranking Details</summary>
          <pre>{{ JSON.stringify(searchResult.debug.reranking, null, 2) }}</pre>
        </details>
      </div>
  
      <!-- Error Display -->
      <div v-if="error" class="error-panel">
        <i class="fas fa-exclamation-triangle"></i>
        {{ error }}
      </div>
  
      <!-- Loading Overlay -->
      <div v-if="isLoading" class="loading-overlay">
        <div class="loading-content">
          <i class="fas fa-cog fa-spin fa-2x"></i>
          <p>{{ loadingMessage }}</p>
        </div>
      </div>
    </div>
  </template>
  
  <script setup>
  import { ref } from 'vue'
  import axios from 'axios'
  
  // State
  const query = ref('')
  const useReranking = ref(true)
  const showDebug = ref(false)
  const retrievalSize = ref(50)
  const rerankSize = ref(20)
  const searchResult = ref(null)
  const error = ref(null)
  const isLoading = ref(false)
  const loadingMessage = ref('')
  
  // API base URL (adjust as needed)
  const API_BASE = '/api/v1/'
  
  // Methods
  const runSearch = async () => {
    if (!query.value.trim()) return
    
    error.value = null
    isLoading.value = true
    loadingMessage.value = 'Expanding query...'
    
    try {
      loadingMessage.value = useReranking.value 
        ? 'Retrieving & reranking...' 
        : 'Searching...'
      
      const response = await axios.post('api/v1/nl_test/search', {
        query: query.value,
        retrieval_size: retrievalSize.value,
        rerank_size: rerankSize.value,
        use_reranking: useReranking.value,
        debug: showDebug.value
      },{timeout: 120000  // 2 minutes
        })
      
      searchResult.value = response.data
    } catch (err) {
      console.error('Search error:', err)
      error.value = err.response?.data?.detail || err.message || 'Search failed'
    } finally {
      isLoading.value = false
      loadingMessage.value = ''
    }
  }
  
  const truncate = (text, length) => {
    if (!text) return ''
    return text.length > length ? text.slice(0, length) + '...' : text
  }
  </script>
  
  <style scoped>
  .nl-test-panel {
    max-width: 1200px;
    margin: 0 auto;
    padding: 2rem;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }
  
  .test-header {
    margin-bottom: 2rem;
  }
  
  .test-header h2 {
    font-size: 1.5rem;
    color: var(--text-color, #333);
    margin: 0;
  }
  
  .test-header h2 i {
    color: var(--primary-color, #d42f3d);
    margin-right: 0.5rem;
  }
  
  .subtitle {
    color: var(--text-muted, #666);
    margin: 0.25rem 0 0;
    font-size: 0.9rem;
  }
  
  /* Search Section */
  .search-section {
    background: var(--surface-color, #f5f5f5);
    border-radius: 0.75rem;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }
  
  .input-group {
    display: flex;
    gap: 0.75rem;
  }
  
  .query-input {
    flex: 1;
    padding: 0.875rem 1rem;
    font-size: 1rem;
    border: 2px solid var(--border-color, #ddd);
    border-radius: 0.5rem;
    background: var(--bg-color, #fff);
    color: var(--text-color, #333);
    transition: border-color 0.2s;
  }
  
  .query-input:focus {
    outline: none;
    border-color: var(--primary-color, #d42f3d);
  }
  
  .search-btn {
    padding: 0.875rem 1.5rem;
    background: var(--primary-color, #d42f3d);
    color: white;
    border: none;
    border-radius: 0.5rem;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: opacity 0.2s;
  }
  
  .search-btn:hover:not(:disabled) {
    opacity: 0.9;
  }
  
  .search-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  .options-row {
    display: flex;
    gap: 1.5rem;
    margin-top: 1rem;
    flex-wrap: wrap;
    align-items: center;
  }
  
  .option {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    color: var(--text-color, #333);
    cursor: pointer;
  }
  
  .option input[type="checkbox"] {
    width: 1rem;
    height: 1rem;
    cursor: pointer;
  }
  
  .size-inputs {
    display: flex;
    gap: 1rem;
    margin-left: auto;
  }
  
  .size-inputs label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.85rem;
    color: var(--text-muted, #666);
  }
  
  .size-inputs input[type="number"] {
    width: 60px;
    padding: 0.25rem 0.5rem;
    border: 1px solid var(--border-color, #ddd);
    border-radius: 0.25rem;
    background: var(--bg-color, #fff);
    color: var(--text-color, #333);
  }
  
  /* Summary Bar */
  .summary-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    padding: 1rem;
    background: var(--surface-color, #f5f5f5);
    border-radius: 0.5rem;
    margin-bottom: 1rem;
  }
  
  .summary-item {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }
  
  .summary-item .label {
    font-size: 0.7rem;
    text-transform: uppercase;
    color: var(--text-muted, #666);
    letter-spacing: 0.5px;
  }
  
  .summary-item .value {
    font-size: 0.9rem;
    font-weight: 500;
    color: var(--text-color, #333);
  }
  
  .summary-item .value.highlight {
    color: var(--primary-color, #d42f3d);
  }
  
  /* Rerank Reasoning */
  .rerank-reasoning {
    background: rgba(76, 175, 80, 0.1);
    border: 1px solid rgba(76, 175, 80, 0.3);
    border-radius: 0.5rem;
    padding: 1rem;
    margin-bottom: 1rem;
  }
  
  .rerank-reasoning h4 {
    margin: 0 0 0.5rem;
    font-size: 0.9rem;
    color: #2e7d32;
  }
  
  .rerank-reasoning h4 i {
    margin-right: 0.5rem;
  }
  
  .rerank-reasoning p {
    margin: 0;
    font-size: 0.85rem;
    color: var(--text-color, #333);
    line-height: 1.5;
  }
  
  /* Results List */
  .results-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  
  .result-card {
    background: var(--bg-color, #fff);
    border: 1px solid var(--border-color, #ddd);
    border-radius: 0.75rem;
    padding: 1rem;
    transition: box-shadow 0.2s;
  }
  
  .result-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  .result-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
  }
  
  .rank {
    background: var(--primary-color, #d42f3d);
    color: white;
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8rem;
    font-weight: 600;
  }
  
  .name {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--text-color, #333);
  }
  
  .score {
    margin-left: auto;
    background: var(--surface-color, #f5f5f5);
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--text-muted, #666);
  }
  
  .job-titles {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }
  
  .job-title-chip {
    background: rgba(33, 150, 243, 0.1);
    color: #1976d2;
    padding: 0.25rem 0.75rem;
    border-radius: 1rem;
    font-size: 0.8rem;
    font-weight: 500;
  }
  
  .skills-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
    margin-bottom: 0.75rem;
  }
  
  .skill-chip {
    background: var(--surface-color, #f5f5f5);
    color: var(--text-color, #333);
    padding: 0.2rem 0.5rem;
    border-radius: 0.25rem;
    font-size: 0.75rem;
  }
  
  .more {
    color: var(--text-muted, #666);
    font-size: 0.75rem;
    font-style: italic;
  }
  
  .summary {
    font-size: 0.85rem;
    color: var(--text-muted, #666);
    line-height: 1.5;
    margin: 0 0 0.75rem;
  }
  
  .meta-row {
    display: flex;
    gap: 1rem;
    font-size: 0.7rem;
  }
  
  .candidate-id, .doc-id {
    background: var(--surface-color, #f5f5f5);
    padding: 0.2rem 0.4rem;
    border-radius: 0.2rem;
    font-family: monospace;
    color: var(--text-muted, #666);
  }
  
  /* Debug Panel */
  .debug-panel {
    margin-top: 2rem;
    background: #1e1e1e;
    border-radius: 0.75rem;
    padding: 1rem;
    color: #d4d4d4;
  }
  
  .debug-panel h3 {
    margin: 0 0 1rem;
    font-size: 1rem;
    color: #9cdcfe;
  }
  
  .debug-panel h3 i {
    margin-right: 0.5rem;
  }
  
  .debug-panel details {
    margin-bottom: 0.5rem;
  }
  
  .debug-panel summary {
    cursor: pointer;
    padding: 0.5rem;
    background: #2d2d2d;
    border-radius: 0.25rem;
    font-size: 0.85rem;
    color: #ce9178;
  }
  
  .debug-panel summary:hover {
    background: #3c3c3c;
  }
  
  .debug-panel pre {
    background: #2d2d2d;
    padding: 1rem;
    border-radius: 0.25rem;
    overflow-x: auto;
    font-size: 0.75rem;
    line-height: 1.4;
    max-height: 400px;
    overflow-y: auto;
  }
  
  /* Error Panel */
  .error-panel {
    background: rgba(244, 67, 54, 0.1);
    border: 1px solid rgba(244, 67, 54, 0.3);
    color: #c62828;
    padding: 1rem;
    border-radius: 0.5rem;
    margin-top: 1rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  
  /* Loading Overlay */
  .loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }
  
  .loading-content {
    background: var(--bg-color, #fff);
    padding: 2rem;
    border-radius: 0.75rem;
    text-align: center;
  }
  
  .loading-content i {
    color: var(--primary-color, #d42f3d);
    margin-bottom: 1rem;
  }
  
  .loading-content p {
    margin: 0;
    color: var(--text-color, #333);
  }
  </style>